import Head from 'next/head'
import type { ShopSeoData } from '../seo'
import { toAbsoluteUrl } from '../seo'
import {
  DEFAULT_SHOP_LOCALE,
  normalizeShopLocale,
  withLocalePrefix,
  type ShopLocale,
} from '../../i18n/shopLocale'
import { toSafeJsonScript } from '../../shared/security/safeBrowserOutput'

type ShopSeoHeadProps = {
  seo: ShopSeoData
  siteUrl: string
  siteName?: string
  faviconUrl?: string
  locale?: ShopLocale
}

const DEFAULT_SITE_NAME = 'Ecommerce'

const ShopSeoHead = ({
  seo,
  siteUrl,
  siteName = DEFAULT_SITE_NAME,
  faviconUrl = '/favicon.ico',
  locale = DEFAULT_SHOP_LOCALE,
}: ShopSeoHeadProps) => {
  const canonicalLocale = seo.canonicalLocale ?? locale
  const fallbackAlternates: Array<{ locale: ShopLocale; path: string }> = [
    { locale: 'it-IT', path: seo.canonicalPath },
    { locale: 'en-US', path: seo.canonicalPath },
  ]
  const alternates = (seo.alternates?.length ? seo.alternates : fallbackAlternates).filter((entry) => {
    const resolvedLocale = normalizeShopLocale(entry.locale)
    return Boolean(resolvedLocale && entry.path?.trim())
  })
  const canonicalPath = withLocalePrefix(seo.canonicalPath, canonicalLocale)
  const canonicalUrl = toAbsoluteUrl(siteUrl, canonicalPath)
  const xDefaultPath =
    seo.xDefaultPath ??
    alternates.find((entry) => entry.locale === DEFAULT_SHOP_LOCALE)?.path ??
    seo.canonicalPath
  const imageUrl = seo.image ? toAbsoluteUrl(siteUrl, seo.image) : ''
  const robots = seo.noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

  const breadcrumbJsonLd =
    Array.isArray(seo.breadcrumbs) && seo.breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: seo.breadcrumbs.map((entry, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: entry.name,
            item: toAbsoluteUrl(siteUrl, withLocalePrefix(entry.path, canonicalLocale)),
          })),
        }
      : null

  const productJsonLd =
    seo.pageType === 'product'
      ? {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: seo.entityName || seo.title,
          description: seo.description,
          ...(imageUrl ? { image: [imageUrl] } : {}),
          ...(seo.product?.sku ? { sku: seo.product.sku } : {}),
          ...(seo.product?.brand
            ? {
                brand: {
                  '@type': 'Brand',
                  name: seo.product.brand,
                },
              }
            : {}),
          ...(seo.product?.price
            ? {
                offers: {
                  '@type': 'Offer',
                  priceCurrency: seo.product.currency || 'EUR',
                  price: seo.product.price,
                  availability:
                    seo.product.availability || 'https://schema.org/InStock',
                  url: canonicalUrl,
                },
              }
            : {}),
        }
      : null

  return (
    <Head>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="icon" href={faviconUrl} />

      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={seo.ogType} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonicalUrl} />
      {seo.localeFallback && (
        <meta
          name="emporion:locale-fallback"
          content={`${seo.localeFallback.requestedLocale}->${seo.localeFallback.resolvedLocale}`}
        />
      )}
      {imageUrl && <meta property="og:image" content={imageUrl} />}

      <meta name="twitter:card" content={imageUrl ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {seo.pageType === 'product' && seo.product?.price && (
        <>
          <meta property="product:price:amount" content={String(seo.product.price)} />
          <meta property="product:price:currency" content={seo.product.currency || 'EUR'} />
        </>
      )}

      {alternates.map((entry) => (
        <link
          key={`${entry.locale}:${entry.path}`}
          rel="alternate"
          hrefLang={entry.locale}
          href={toAbsoluteUrl(siteUrl, withLocalePrefix(entry.path, entry.locale))}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={toAbsoluteUrl(siteUrl, withLocalePrefix(xDefaultPath, DEFAULT_SHOP_LOCALE))}
      />

      {breadcrumbJsonLd && (
        <script
          type="application/ld+json"
          // JSON-LD must be injected as raw script content.
          dangerouslySetInnerHTML={toSafeJsonScript(breadcrumbJsonLd)}
        />
      )}
      {productJsonLd && (
        <script
          type="application/ld+json"
          // JSON-LD must be injected as raw script content.
          dangerouslySetInnerHTML={toSafeJsonScript(productJsonLd)}
        />
      )}
    </Head>
  )
}

export default ShopSeoHead
